import React, { useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import { Modal } from "react-bootstrap";
import {
    CreditCard,
    CheckCircle, AlertCircle, Clock, XCircle,
} from "lucide-react";
import Spinner from "../components/Spinner";
import { PageHeader } from "../components/ui/bootstrap";
import UsageThisMonth from "../components/plans/UsageThisMonth";
import CurrentPlanCard from "../components/plans/CurrentPlanCard";
import BillingModal from "../components/plans/BillingModal";
import ChooseAPlan from "../components/plans/ChooseAPlan";
import { getPlanTitle } from "../components/plans/PlanCardsGrid";
import EnterpriseStrip from "../components/plans/EnterpriseStrip";
import CreditRatesCard from "../components/plans/CreditRatesCard";
import ComparePlans from "../components/plans/ComparePlans";
import BillingFAQ from "../components/plans/BillingFAQ";
import { FREE_AND_PREVIEW_PLANS } from "../constants/constants";
import { STATUS_CONFIG } from "../constants/theme";
import { useSubscription } from "../http-hooks/organisation/useSubscription";
import { useOrganisation, useOrganisationUsage } from "../http-hooks/organisation/useOrganisation";
import { useBilling } from "../hooks/useBilling";
import {
    dashCaseToSentenceCase,
    displayAmount,
    formatSubscriptionStatus,
    planIdToString,
} from "../utils/billingFormat";
import BillingSkeleton from "../components/skeletons/pages/BillingSkeleton";

const statusConfig = {
    active: { icon: CheckCircle, ...STATUS_CONFIG.active },
    in_trial: { icon: Clock, ...STATUS_CONFIG.in_trial },
    non_renewing: { icon: AlertCircle, ...STATUS_CONFIG.non_renewing, label: "Cancelled" },
    cancelled: { icon: XCircle, ...STATUS_CONFIG.cancelled },
};

export default function Billing() {
    const { org_id } = useParams();
    const history = useHistory();

    const { data: organisation } = useOrganisation({ org_id });
    const { data: orgUsage } = useOrganisationUsage({ org_id });
    const { isLoading, data } = useSubscription({ org_id });

    const [show, setShow] = useState(false);
    const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [cancelSuggestion, setCancelSuggestion] = useState("");
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [isResumingSubscription, setIsResumingSubscription] = useState(false);
    const [showBillingModal, setShowBillingModal] = useState(false);
    const plansRef = useRef(null);

    const { openPortal, cancelSubscription, resumeSubscription } = useBilling({ orgId: org_id });

    const onOpenPortal = async (sectionType = "ACCOUNT_DETAILS") => {
        try {
            await openPortal({ sectionType, redirectURL: window.location.href });
        } catch (err) { /* toasted in useBilling */ }
    };

    const onCancelSubscription = () => {
        setIsCancellingSubscription(true);
        cancelSubscription({ cancelReason, cancelSuggestion })
            .then((resp) => {
                setIsCancellingSubscription(false);
                if (resp?.skipped) return;
                history.go(0);
            })
            .catch(() => setIsCancellingSubscription(false));
    };

    const onResumeSubscription = () => {
        setIsResumingSubscription(true);
        resumeSubscription()
            .then(() => {
                setIsResumingSubscription(false);
                setShowResumeModal(false);
                history.go(0);
            })
            .catch(() => setIsResumingSubscription(false));
    };

    if (isLoading || !organisation || !data) {
        return <BillingSkeleton />;
    }

    if (!organisation.plan_id) {
        history.push(`/organisation/${org_id}/plans`);
        return null;
    }

    const subscription = data;
    const lastPayment = subscription.invoices.list.length
        ? subscription.invoices.list[0].invoice
        : null;

    const isFree = FREE_AND_PREVIEW_PLANS.includes(organisation.plan_id);
    const planName = getPlanTitle(organisation.plan_id) || dashCaseToSentenceCase(planIdToString(organisation.plan_id));
    const status = subscription.status;
    const statusInfo = statusConfig[status] || statusConfig.active;
    const StatusIcon = statusInfo.icon;

    const wordsLimit = orgUsage?.plan_credits_limit ?? orgUsage?.plan_words_limit ?? 0;
    const wordsRemaining = orgUsage?.credits_remaining ?? orgUsage?.words_remaining ?? 0;
    const wordsUsed = wordsLimit - wordsRemaining;

    const resetDate = orgUsage?.renews_on && !isFree ? moment(orgUsage.renews_on).format("MMMM D, YYYY") : null;
    const canCancel = status !== "non_renewing";

    const nextPayment = (status === "cancelled" || status === "non_renewing")
        ? null
        : subscription.next_billing_at
            ? {
                date: moment.unix(subscription.next_billing_at).format("MMM D, YYYY"),
                amount: displayAmount(subscription.currency_code, subscription.mrr || subscription.plan_amount),
            }
            : null;
    const lastPaymentInfo = lastPayment
        ? {
            date: moment.unix(lastPayment.date).format("MMM D, YYYY"),
            amount: displayAmount(lastPayment.currency_code, lastPayment.amount_paid),
        }
        : null;

    const scrollToPlans = () => plansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

    return (
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
            {/* Cancel Modal */}
            <Modal show={show} onHide={() => setShow(false)} centered>
                <Modal.Body style={{ padding: "32px 28px 20px" }}>
                    <div className="text-center mb-4">
                        <div style={{ fontSize: "3rem", marginBottom: 12 }}>😢</div>
                        <h5 style={{ fontWeight: 700, color: "var(--bs-emphasis-color)", marginBottom: 8 }}>We're sorry to see you go</h5>
                        <p style={{ fontSize: "13px", color: "var(--bs-secondary-color)", marginBottom: 0 }}>
                            Before you cancel, please let us know why. Every bit of feedback helps!
                        </p>
                    </div>
                    <div className="mb-3">
                        <textarea
                            placeholder="Please tell us why you're cancelling *"
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            className="form-control"
                            rows={3}
                            style={{ fontSize: "13px", borderRadius: "10px", border: "1px solid rgba(var(--bs-primary-rgb), 0.18)", resize: "vertical" }}
                        />
                    </div>
                    <div className="mb-3">
                        <textarea
                            placeholder="Any suggestions for us? (Optional)"
                            value={cancelSuggestion}
                            onChange={(e) => setCancelSuggestion(e.target.value)}
                            className="form-control"
                            rows={2}
                            style={{ fontSize: "13px", borderRadius: "10px", border: "1px solid rgba(var(--bs-primary-rgb), 0.18)", resize: "vertical" }}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ padding: "16px 28px", borderTop: "1px solid rgba(var(--bs-primary-rgb), 0.10)", flexDirection: "column", gap: 8 }}>
                    <p style={{ fontSize: "11px", color: "var(--bs-secondary-color)", textAlign: "center", margin: "0 0 8px" }}>
                        On confirmation, your subscription will be cancelled at the end of the current billing period.
                    </p>
                    <div className="d-flex gap-2 w-100">
                        <button
                            className="btn flex-fill"
                            onClick={() => setShow(false)}
                            disabled={isCancellingSubscription}
                            style={{ borderRadius: "10px", fontSize: "13px", fontWeight: 600, border: "1px solid rgba(var(--bs-primary-rgb), 0.20)", color: "var(--bs-secondary-color)" }}
                        >
                            Keep My Plan
                        </button>
                        <button
                            className="btn flex-fill"
                            disabled={!cancelReason.length || isCancellingSubscription}
                            onClick={onCancelSubscription}
                            style={{
                                borderRadius: "10px", fontSize: "13px", fontWeight: 600,
                                background: cancelReason.length ? "linear-gradient(135deg, var(--bs-danger) 0%, rgba(var(--bs-danger-rgb), 0.6) 100%)" : "rgba(var(--bs-danger-rgb), 0.08)",
                                color: cancelReason.length ? "white" : "var(--bs-secondary-color)",
                                border: "none",
                                boxShadow: cancelReason.length ? "0 4px 12px rgba(var(--bs-danger-rgb), 0.30)" : "none",
                            }}
                        >
                            {isCancellingSubscription ? <Spinner color="white" /> : "Confirm Cancellation"}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Resume Subscription Modal */}
            <Modal show={showResumeModal} onHide={() => setShowResumeModal(false)} centered>
                <Modal.Body style={{ padding: "32px 28px 20px" }}>
                    <div className="text-center mb-4">
                        <div style={{ fontSize: "3rem", marginBottom: 12 }}>🎉</div>
                        <h5 style={{ fontWeight: 700, color: "var(--bs-emphasis-color)", marginBottom: 8 }}>Resume your subscription</h5>
                        <p style={{ fontSize: "13px", color: "var(--bs-secondary-color)", marginBottom: 0 }}>
                            Your subscription will continue and you will be billed at the next renewal date. Are you sure you want to resume?
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ padding: "16px 28px", borderTop: "1px solid rgba(var(--bs-primary-rgb), 0.10)" }}>
                    <div className="d-flex gap-2 w-100">
                        <button
                            className="btn flex-fill"
                            onClick={() => setShowResumeModal(false)}
                            disabled={isResumingSubscription}
                            style={{ borderRadius: "10px", fontSize: "13px", fontWeight: 600, border: "1px solid rgba(var(--bs-primary-rgb), 0.20)", color: "var(--bs-secondary-color)" }}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn flex-fill"
                            onClick={onResumeSubscription}
                            disabled={isResumingSubscription}
                            style={{
                                borderRadius: "10px", fontSize: "13px", fontWeight: 600,
                                background: "linear-gradient(135deg, var(--bs-primary) 0%, rgba(var(--bs-primary-rgb), 0.7) 100%)",
                                color: "white",
                                border: "none",
                                boxShadow: "0 4px 12px rgba(var(--bs-primary-rgb), 0.30)",
                            }}
                        >
                            {isResumingSubscription ? <Spinner color="white" /> : "Confirm Resume"}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Page Header */}
            <PageHeader
                icon={CreditCard}
                title="Plan & Billing"
                subtitle="Manage your subscription and payment details"
            />

            {/* ── Credits used + Currently on plan / Billing ── */}
            {/* CurrentPlanCard is the only entry point to BillingModal (cancel/payment/address/
                invoices), so it must render even if usage data failed to load or wordsLimit is 0 —
                only the usage bar itself depends on that data. */}
            <div className="billing-summary-cards mb-4">
                {wordsLimit > 0 && <UsageThisMonth used={wordsUsed} total={wordsLimit} />}
                <CurrentPlanCard
                    planName={planName}
                    onOpenBilling={() => setShowBillingModal(true)}
                    style={wordsLimit > 0 ? undefined : { gridColumn: "1 / -1" }}
                />
            </div>

            {/* ── Choose a Plan ── */}
            <div ref={plansRef}>
                <ChooseAPlan />
            </div>

            {/* Enterprise strip — no in-product Enterprise plan, just a Crisp chat CTA */}
            <EnterpriseStrip />

            {showBillingModal && (
                <BillingModal
                    onClose={() => setShowBillingModal(false)}
                    planName={planName}
                    creditsUsed={wordsUsed}
                    creditsTotal={wordsLimit}
                    resetDate={resetDate}
                    statusInfo={statusInfo}
                    StatusIcon={StatusIcon}
                    statusLabel={formatSubscriptionStatus(subscription.status)}
                    isFree={isFree}
                    nextPayment={nextPayment}
                    lastPayment={lastPaymentInfo}
                    onOpenPortal={onOpenPortal}
                    onManagePlans={scrollToPlans}
                    canCancel={canCancel}
                    onCancelClick={() => setShow(true)}
                />
            )}

            {/* ── How credits are used / Compare plans / FAQ ── */}
            <div className="mt-4">
                <CreditRatesCard />
                <ComparePlans />
                <BillingFAQ />
            </div>
        </div>
    );
}
